import React, { useEffect, useState } from "react";
import { resolveLocation } from "../api/locations";
import SharedSelect from "./SharedSelect";
import { useToast } from "./ToastProvider";
import type { LocationSnapshot } from "../types/location";
import { DEFAULT_BROWSING_RADIUS, LOCATION_RADIUS_OPTIONS } from "../utils/location";
import "../styles/LocationPickerModal.css";

type Props = {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  confirmLabel?: string;
  initialLocation?: LocationSnapshot | null;
  initialRadiusMiles?: number;
  showRadius?: boolean;
  onClose: () => void;
  onConfirm: (location: LocationSnapshot, radiusMiles?: number) => Promise<void> | void;
};

export default function LocationPickerModal({
  isOpen,
  title,
  subtitle,
  confirmLabel = "Save location",
  initialLocation,
  initialRadiusMiles = DEFAULT_BROWSING_RADIUS,
  showRadius = false,
  onClose,
  onConfirm,
}: Props) {
  const { showToast } = useToast();
  const [postalCode, setPostalCode] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [radiusMiles, setRadiusMiles] = useState(initialRadiusMiles);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPostalCode(initialLocation?.postalCode || "");
    setCountryCode(initialLocation?.countryCode || "US");
    setRadiusMiles(initialRadiusMiles);
    setSaving(false);
  }, [initialLocation, initialRadiusMiles, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!postalCode.trim()) {
      showToast("Enter a postal code first.", "warning");
      return;
    }

    setSaving(true);
    try {
      const location = await resolveLocation(postalCode, countryCode);
      await onConfirm(location, showRadius ? radiusMiles : undefined);
      onClose();
    } catch (submitError: any) {
      showToast(submitError?.message || "Could not save that location.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content location-picker-modal" onClick={(event: any) => event.stopPropagation()}>
        <h2>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}

        <form className="location-picker-form" onSubmit={handleSubmit}>
          <label className="location-picker-field">
            <span>Postal code</span>
            <input
              type="text"
              value={postalCode}
              onChange={(event: any) => setPostalCode(event.target.value.slice(0, 16))}
              placeholder="Enter postal code"
              autoFocus
            />
          </label>

          <label className="location-picker-field">
            <span>Country code (e.g. US)</span>
            <input
              type="text"
              value={countryCode}
              onChange={(event: any) => setCountryCode(event.target.value.slice(0, 2).toUpperCase())}
              placeholder="US"
              maxLength={2}
            />
          </label>

          {showRadius ? (
            <label className="location-picker-field">
              <span>Browsing radius</span>
              <SharedSelect
                className="location-picker-select"
                value={radiusMiles}
                onChange={(event: any) => setRadiusMiles(Number(event.target.value))}
                aria-label="Browsing radius"
              >
                {LOCATION_RADIUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} miles
                  </option>
                ))}
              </SharedSelect>
            </label>
          ) : null}

          <div className="location-picker-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? "Saving..." : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
